import { useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/session.tsx';
import {
  realEstateFolder, downloadUpload, ensurePath, fetchUpload, useCreateFolder, useRefreshFolders, type ClientFolders, type ClientUpload,
} from '../api/uploads.ts';
import { downloadClientDocument, fetchDocument, type ClientDocument } from '../api/documents.ts';
import { fromDrop, fromPicker, uploadAll, type PickedFile, type UploadResult } from '../folderUpload.ts';
import { procuraTarget, type MergedDocuments, type MergedFolder } from '../documentsMerge.ts';
import { backLabel, titleOf, trail, up } from '../folderNav.ts';
import { findByData } from '../dataLookup.ts';
import { uploadViewer, viewerFor } from '../textPreview.ts';
import { DocumentPreview, type PreviewDoc } from './DocumentPreview.tsx';
import { offeredTopFolders } from '../folderCatalogue.ts';
import { AddTopFolder } from './AddTopFolder.tsx';

/**
 * A client's documents, one folder at a time (142, 143, the merged view; the navigation, Arthur, 21 October, 16:12):
 * the top level is the four folders every client has, Real Estate where the client has a property or Clio has it (or
 * once added from the catalogue, 145), and the read-only "Folders taken directly from Clio"; opening a folder replaces the list with what is in it. The bar says where the
 * reader is: a back button on the left named for the folder above ("<- Documents" inside a top folder; none at the
 * top), the folder's own name in the middle, and at the top only a "+" on the right that adds a folder from the
 * catalogue (145), shown only when the reader may add one the client lacks. Inside a folder: its folders, then its files (name, date, who
 * uploaded it; Clio's marked From Clio), and Upload files, Upload a folder (chosen, or dropped on the list) and New
 * folder inside, 8 deep at most, never at the top and never in the Clio group. What is uploaded lands in the folder the
 * reader is in, and the reader stays there. A file says "Checking for viruses..." until Defender answers and opens
 * only when clean; a file's name opens it in the preview beside the list. No delete, rename or move.
 */
export function ClientFoldersPanel({ clientId, data, merged }: {
  clientId: string; data: ClientFolders; merged: MergedDocuments;
}) {
  const { getToken } = useSession();
  const [path, setPath] = useState<string[]>([]);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ id: string; message: string } | null>(null);
  const [returnTo, setReturnTo] = useState<string | null>(null);
  useEffect(() => {
    if (returnTo === null) return;
    // Never a selector built from the key: a folder's key is JSON, and its quotes broke one (dataLookup.ts).
    findByData<HTMLButtonElement>(document, 'doc-key', returnTo)?.focus();
    setReturnTo(null);
  }, [returnTo]);

  // Where the reader is, found again by name each time the list arrives, so an upload never moves them.
  const at = trail(merged.folders, path);
  const here = at.length > 0 ? at[at.length - 1]! : null;
  const back = backLabel(at);
  const offered = offeredTopFolders(data.addable ?? [], merged.folders);

  // The files of this folder, uploads and Clio copies alike: what Previous and Next walk.
  const openable: Item[] = here ? [
    ...here.uploads.filter((u) => u.openable).map((u): Item => ({ key: `u:${u.id}`, kind: 'upload', upload: u })),
    ...here.clio.filter((d) => d.openable).map((d): Item => ({ key: `c:${d.id}`, kind: 'clio', doc: d })),
  ] : [];
  const shown = openable.find((i) => i.key === previewing) ?? null;
  const pos = shown ? openable.findIndex((i) => i.key === shown.key) : -1;

  const download = (item: Item) => {
    if (busy === item.key) return;
    setBusy(item.key); setFailed(null);
    const go = item.kind === 'upload' ? downloadUpload(getToken, clientId, item.upload.id) : downloadClientDocument(getToken, clientId, item.doc.id);
    void go.catch((e: Error) => setFailed({ id: item.key, message: e.message })).finally(() => setBusy(null));
  };
  const preview = (key: string) => {
    setPreviewing(key);
    requestAnimationFrame(() => document.getElementById('document-preview')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  };
  const go = (to: string[], focus: string | null) => {
    setPreviewing(null); setFailed(null); setPath(to); setReturnTo(focus);
  };
  const open = (name: string) => go([...at.map((f) => f.name), name], 'folder-back');
  const goBack = () => { const from = here?.key ?? null; go(up(at.map((f) => f.name)), from ? `folder:${from}` : null); };

  const ctx: Ctx = {
    clientId, folders: merged.folders, mayUpload: data.may_upload, limit: data.limit_bytes, maxDepth: data.max_depth,
    current: shown?.key ?? null, onPreview: preview, onDownload: download, busy, failed,
  };

  return (
    <section className="panel" aria-labelledby="documents-where">
      <div className="grid min-h-[56px] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 bg-brand px-2 py-1 text-on-brand">
        <div className="min-w-0">
          {back !== null && (
            <button type="button" onClick={goBack} data-doc-key="folder-back"
                    className="inline-flex min-h-[44px] max-w-full items-center gap-1 rounded-lg px-3 font-semibold hover:bg-on-brand/10">
              <span aria-hidden="true">←</span>
              <span className="truncate"><span className="sr-only">Back to </span>{back}</span>
            </button>
          )}
        </div>
        <h2 id="documents-where" className="text-center text-lg font-semibold break-words">{titleOf(at)}</h2>
        <div className="flex min-w-0 justify-end">
          {at.length === 0 && offered.length > 0 && <AddTopFolder clientId={clientId} offered={offered} />}
        </div>
      </div>
      <div className={shown ? 'grid gap-4 px-4 pt-3 pb-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]' : 'px-4 pt-3 pb-4'}>
        {shown ? (
          <DocumentPreview doc={asPreview(shown)}
                           fetchBlob={() => (shown.kind === 'upload' ? fetchUpload(getToken, clientId, shown.upload.id) : fetchDocument(getToken, clientId, shown.doc.id))}
                           position={pos >= 0 ? { at: pos, of: openable.length } : null}
                           onStep={(step) => { if (pos >= 0 && openable.length > 1) setPreviewing(openable[(pos + step + openable.length) % openable.length]!.key); }}
                           onClose={() => { setPreviewing(null); setReturnTo(shown.key); }} onDownload={() => download(shown)}
                           downloading={busy === shown.key} failed={failed?.id === shown.key ? failed.message : null} />
        ) : null}
        <div className="min-w-0 space-y-3 lg:order-first">
          {/* Keyed by the folder's place, so its upload count survives the list arriving again after an upload. */}
          <FolderView key={here?.key ?? 'top'} node={here} tops={merged.folders} ctx={ctx} onOpen={open} />
          <p className="text-sm text-neutral-600">
            Files are checked for viruses before they can be opened. Files marked From Clio are copies of Clio’s, shown
            where they belong and never changed here; Folders taken directly from Clio holds Clio’s other folders as
            Clio has them, read-only. Choose a file’s name to preview it. Uploading, previewing or downloading is
            recorded.
          </p>
        </div>
      </div>
    </section>
  );
}

type Item = { key: string; kind: 'upload'; upload: ClientUpload } | { key: string; kind: 'clio'; doc: ClientDocument };

interface Ctx {
  clientId: string; folders: MergedFolder[];
  mayUpload: boolean; limit: number; maxDepth: number; current: string | null;
  onPreview: (key: string) => void; onDownload: (item: Item) => void; busy: string | null;
  failed: { id: string; message: string } | null;
}

/** What one folder holds (or, with no folder, the top level), and what may be done there. */
function FolderView({ node, tops, ctx, onOpen }: {
  node: MergedFolder | null; tops: MergedFolder[]; ctx: Ctx; onOpen: (name: string) => void;
}) {
  const { getToken } = useSession();
  const refresh = useRefreshFolders(ctx.clientId);
  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [over, setOver] = useState(false);
  const folders = node ? node.children : tops;
  // Never at the top (the top level is fixed, 14:03), never in the Clio group (14:31).
  const writable = ctx.mayUpload && node !== null && !node.readOnly;
  const roomBelow = node !== null && node.depth < ctx.maxDepth;
  const count = node ? node.uploads.length + node.clio.length : 0;
  const inputId = (node?.key ?? 'top').replace(/[^a-z0-9]+/gi, '-');

  /** The Procura folder here, made where only Clio has one (Real Estate's top first, then the names below). */
  const target = async (): Promise<string> => {
    if (node!.procuraId) return node!.procuraId;
    const t = procuraTarget(ctx.folders, node!.path);
    // The only top folder ever made here is Real Estate, and the server checks the client's Clio has it.
    let id = t.id ?? (await realEstateFolder(getToken, ctx.clientId)).id;
    if (t.below.length > 0) id = (await ensurePath(getToken, ctx.clientId, id, t.below)).id;
    return id;
  };

  const run = async (picked: PickedFile[]) => {
    if (!node || picked.length === 0 || progress) return;
    setResult(null);
    if (picked.some((p) => node.depth + p.path.length > ctx.maxDepth)) {
      setResult({ uploaded: 0, failed: [{ name: 'that folder', why: `it would go deeper than ${ctx.maxDepth} folders` }], skipped: 0 });
      return;
    }
    setProgress({ done: 0, total: picked.length });
    let r: UploadResult;
    try {
      const id = await target();
      r = await uploadAll(getToken, ctx.clientId, id, picked, ctx.limit, (done, total) => setProgress({ done, total }));
    } catch (e) {
      r = { uploaded: 0, failed: [{ name: node.name, why: `the folder could not be made: ${(e as Error).message}` }], skipped: 0 };
    }
    setProgress(null);
    setResult(r);
    // The list is fetched again; the panel finds this folder by name and the reader stays in it.
    void refresh();
    if (filesInput.current) filesInput.current.value = '';
    if (folderInput.current) folderInput.current.value = '';
  };

  return (
    <div onDragOver={(e) => { if (writable) { e.preventDefault(); setOver(true); } }}
         onDragLeave={(e) => { if (e.currentTarget === e.target) setOver(false); }}
         onDrop={(e) => {
           if (!writable) return;
           e.preventDefault(); setOver(false);
           void fromDrop(e.dataTransfer.items).then((picked) => run(picked));
         }}
         className={`space-y-3 rounded-lg ${over ? 'outline-2 outline-offset-4 outline-brand' : ''}`}>
      {node?.readOnly && (
        <p className="rounded-lg border-l-8 border-note-edge bg-note-bg px-3 py-2 text-sm text-note-fg">
          Taken directly from Clio, as Clio has it: read-only. Nothing can be uploaded or added here.
        </p>
      )}
      {over && <p className="text-sm font-semibold text-brand-text">Drop to upload into {node?.name}</p>}
      {folders.length > 0 && (
        <ul className="divide-y divide-neutral-100 rounded-lg border border-line bg-panel">
          {folders.map((f) => {
            const files = f.uploads.length + f.clio.length;
            return (
              <li key={f.key}>
                <button type="button" onClick={() => onOpen(f.name)} data-doc-key={`folder:${f.key}`}
                        className="flex min-h-[48px] w-full flex-wrap items-center gap-2 px-3 py-2 text-left hover:bg-neutral-50">
                  <FolderIcon />
                  <span className="font-semibold text-neutral-900">{f.name}</span>
                  {!f.procuraId && <span className={clioTag}>{f.readOnly ? 'From Clio, read-only' : 'From Clio'}</span>}
                  <span className="text-sm text-neutral-600">
                    {f.children.length > 0 ? `${f.children.length} ${f.children.length === 1 ? 'folder' : 'folders'}, ` : ''}
                    {files} {files === 1 ? 'file' : 'files'}
                  </span>
                  <span aria-hidden="true" className="ml-auto text-neutral-500">›</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {node && (count === 0 && folders.length === 0 ? (
        <p className="py-1 text-sm text-neutral-600">No files yet.</p>
      ) : count > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[0.9375rem]">
            <thead className="text-sm text-neutral-500">
              <tr>
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Uploaded by</th>
                <th className="py-2 pr-1 font-medium"><span className="sr-only">Download</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {node.uploads.map((u) => (
                <Row key={`u:${u.id}`} ctx={ctx} item={{ key: `u:${u.id}`, kind: 'upload', upload: u }} name={u.filename}
                     openable={u.openable} state={stateWords(u)}
                     date={new Date(u.uploaded_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                     by={u.uploaded_by ?? '—'} />
              ))}
              {node.clio.map((d) => (
                <Row key={`c:${d.id}`} ctx={ctx} item={{ key: `c:${d.id}`, kind: 'clio', doc: d }} name={d.name}
                     openable={d.openable} state={d.state ?? ''} fromClio
                     date={d.date ? new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                     by="Clio" />
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {progress && <p role="status" className="text-sm font-semibold text-neutral-800">Uploading {progress.done} of {progress.total}…</p>}
      {result && <Summary result={result} onDismiss={() => setResult(null)} />}
      {writable && !progress && (
        <div className="flex flex-wrap gap-2">
          <input ref={filesInput} type="file" multiple className="sr-only" id={`upload-${inputId}`}
                 onChange={(e) => { if (e.target.files) void run(fromPicker(e.target.files).map((p) => ({ ...p, path: [] }))); }} />
          <label htmlFor={`upload-${inputId}`} className={`${secondary} inline-flex cursor-pointer items-center`}>
            Upload files to {node!.name}
          </label>
          {roomBelow && (
            <>
              <input ref={folderInput} type="file" multiple className="sr-only" id={`upload-folder-${inputId}`}
                     {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
                     onChange={(e) => { if (e.target.files) void run(fromPicker(e.target.files)); }} />
              <label htmlFor={`upload-folder-${inputId}`} className={`${secondary} inline-flex cursor-pointer items-center`}>
                Upload a folder
              </label>
              {adding !== null
                ? <NewFolder clientId={ctx.clientId} parentId={adding} onDone={() => { setAdding(null); void refresh(); }} />
                : <button type="button" onClick={() => void target().then((id) => setAdding(id))} className={secondary}>New folder inside</button>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ ctx, item, name, openable, state, date, by, fromClio }: {
  ctx: Ctx; item: Item; name: string; openable: boolean; state: string; date: string; by: string; fromClio?: boolean;
}) {
  const current = ctx.current === item.key;
  return (
    <tr className={current ? 'bg-neutral-100' : undefined}>
      <td className="py-1 pr-4">
        {openable ? (
          <button type="button" data-doc-key={item.key} onClick={() => ctx.onPreview(item.key)} aria-current={current ? 'true' : undefined}
                  className={`min-h-[44px] break-all text-left text-neutral-900 hover:underline ${current ? 'font-semibold' : ''}`}>
            {name}
          </button>
        ) : (
          <span className="block py-2 break-all text-neutral-900">{name}</span>
        )}
        {fromClio && <span className={`${clioTag} ml-2`}>From Clio</span>}
        {!openable && state && <span className="block text-sm font-semibold text-neutral-800">{state}</span>}
        {ctx.failed?.id === item.key && <span role="alert" className="block text-sm text-warning-fg">{ctx.failed.message}</span>}
      </td>
      <td className="py-1 pr-4 whitespace-nowrap">{date}</td>
      <td className="py-1 pr-4 text-neutral-800">{by}</td>
      <td className="py-1 pr-1 text-right">
        {openable && (
          <button type="button" onClick={() => ctx.onDownload(item)} aria-disabled={ctx.busy === item.key}
                  aria-label={`Download ${name}`} className={secondary}>
            {ctx.busy === item.key ? 'Saving…' : 'Download'}
          </button>
        )}
      </td>
    </tr>
  );
}

/** What a batch did, in words and counts; every failure named with why. */
function Summary({ result, onDismiss }: { result: UploadResult; onDismiss: () => void }) {
  return (
    <div role="status" className="rounded-lg border-l-8 border-note-edge bg-note-bg px-3 py-2 text-sm text-note-fg">
      <p className="font-semibold">
        Uploaded {result.uploaded} · Failed {result.failed.length} · Skipped {result.skipped}
        {result.uploaded > 0 ? '. Each is checked for viruses before it opens.' : '.'}
      </p>
      {result.failed.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {result.failed.map((f, i) => <li key={i} className="break-all">{f.name}: {f.why}</li>)}
        </ul>
      )}
      {result.skipped > 0 && <p className="mt-1">Skipped: files an operating system leaves in folders (such as .DS_Store), never documents.</p>}
      <button type="button" onClick={onDismiss} className={`${secondary} mt-2`}>OK</button>
    </div>
  );
}

function stateWords(f: ClientUpload): string {
  switch (f.status) {
    case 'awaiting_scan': return 'Checking for viruses…';
    case 'malicious': return 'Held: a virus was found. It cannot be opened or downloaded.';
    case 'scan_failed': return `Not opened: the virus check could not finish${f.scan_result ? ` (${f.scan_result})` : ''}.`;
    case 'scan_unanswered': return 'Not opened: the virus check never answered.';
    default: return '';
  }
}

function NewFolder({ clientId, parentId, onDone }: { clientId: string; parentId: string; onDone: () => void }) {
  const create = useCreateFolder(clientId);
  const [name, setName] = useState('');
  return (
    <form className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate({ name: name.trim(), parentId }, { onSuccess: onDone }); }}>
      <label className="block text-sm font-semibold text-neutral-700">Folder name
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required autoFocus
               className="mt-1 block min-h-[44px] w-[min(20rem,80vw)] rounded-lg border border-neutral-400 bg-panel px-3 text-base text-neutral-900" />
      </label>
      <button type="submit" disabled={!name.trim() || create.isPending} className={primary}>Add folder</button>
      <button type="button" onClick={onDone} className={secondary}>Cancel</button>
      {create.error && <p role="alert" className="w-full text-sm text-warning-fg">{(create.error as Error).message}</p>}
    </form>
  );
}

const EXT = (name: string) => /\.([^.]+)$/.exec(name)?.[1]?.toLowerCase() ?? '';
const KIND: Record<string, string> = {
  pdf: 'PDF', jpg: 'Photo', jpeg: 'Photo', png: 'Photo', gif: 'Photo', webp: 'Photo', heic: 'Photo', doc: 'Word',
  docx: 'Word', xls: 'Excel', xlsx: 'Excel', msg: 'Email', eml: 'Email', mp4: 'Video', mov: 'Video', txt: 'Text',
  zip: 'Zip', mp3: 'Audio', wav: 'Audio',
};
/** A file as the preview sees it: what it tries by its name; the type it is served as has the last word. */
function asPreview(item: Item): PreviewDoc {
  if (item.kind === 'clio') {
    const d = item.doc;
    return { id: d.id, name: d.name, date: d.date, viewer: viewerFor(d.name, d.viewer), kind: d.kind, bytes: d.bytes };
  }
  const u = item.upload;
  const ext = EXT(u.filename);
  return { id: u.id, name: u.filename, date: u.uploaded_at.slice(0, 10), viewer: uploadViewer(u.filename), kind: KIND[ext] ?? 'File', bytes: u.bytes };
}

function FolderIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 16" className="h-4 w-5 shrink-0 fill-current text-brand-text">
      <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h4.6l1.8 2h8.6A1.5 1.5 0 0 1 19 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 1 13.5z" />
    </svg>
  );
}

const clioTag = 'rounded bg-note-bg px-1.5 py-0.5 text-xs font-semibold text-note-fg';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-400 px-3 text-sm font-semibold text-neutral-900';
const primary = 'min-h-[44px] rounded-lg bg-brand px-4 text-sm font-semibold text-on-brand';
